"use client";

import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";

interface LinkedInputContextType {
  registerInput: (id: string, ref: HTMLInputElement) => void;
  unregisterInput: (id: string) => void;
  focusNext: (currentId: string) => void;
}

const LinkedInputContext = createContext<LinkedInputContextType | null>(null);

export function LinkedInputProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const inputsRef = useRef<Map<string, HTMLInputElement>>(new Map());
  const orderRef = useRef<string[]>([]);

  const registerInput = useCallback((id: string, ref: HTMLInputElement) => {
    inputsRef.current.set(id, ref);
    if (!orderRef.current.includes(id)) {
      orderRef.current.push(id);
    }
  }, []);

  const unregisterInput = useCallback((id: string) => {
    inputsRef.current.delete(id);
    orderRef.current = orderRef.current.filter((inputId) => inputId !== id);
  }, []);

  const focusNext = useCallback((currentId: string) => {
    const currentIndex = orderRef.current.indexOf(currentId);
    if (currentIndex === -1) {
      return; // Not found
    }

    // If this is the last input, submit the form
    if (currentIndex === orderRef.current.length - 1) {
      const currentInput = inputsRef.current.get(currentId);
      if (currentInput) {
        const form = currentInput.closest("form");
        if (form) {
          form.requestSubmit();
        }
      }
      return;
    }

    // Find next input that exists
    for (let i = currentIndex + 1; i < orderRef.current.length; i++) {
      const nextId = orderRef.current[i];
      const nextInput = inputsRef.current.get(nextId);
      if (nextInput) {
        nextInput.focus();
        return;
      }
    }
  }, []);

  const contextValue = useMemo(
    () => ({ registerInput, unregisterInput, focusNext }),
    [registerInput, unregisterInput, focusNext],
  );

  return (
    <LinkedInputContext.Provider value={contextValue}>
      {children}
    </LinkedInputContext.Provider>
  );
}

export function useLinkedInput(id: string) {
  const context = useContext(LinkedInputContext);
  if (!context) {
    return null; // Not in a LinkedInputProvider, that's okay
  }
  return context;
}
